import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Save,
  Coins,
  Home,
  UtensilsCrossed,
  CreditCard,
  Plane,
  Car,
  FileText,
  MoreHorizontal
} from 'lucide-react';
import DateInput from '../common/DateInput';

const ExpenseForm = ({ campaign, editingExpense, onClose, onSave }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    type: '',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const expenseTypes = [
    {
      key: 'accommodationMadinah',
      label: t('expenses.accommodationMadinah'),
      icon: Home,
      color: 'text-blue-600'
    },
    {
      key: 'accommodationMakkah',
      label: t('expenses.accommodationMakkah'),
      icon: Home,
      color: 'text-green-600'
    },
    {
      key: 'mealsMadinah',
      label: t('expenses.mealsMadinah'),
      icon: UtensilsCrossed,
      color: 'text-orange-600'
    },
    {
      key: 'mealsMakkah',
      label: t('expenses.mealsMakkah'),
      icon: UtensilsCrossed,
      color: 'text-red-600'
    },
    {
      key: 'visaFees',
      label: t('expenses.visaFees'),
      icon: CreditCard,
      color: 'text-purple-600'
    },
    {
      key: 'airTickets',
      label: t('expenses.airTickets'),
      icon: Plane,
      color: 'text-indigo-600'
    },
    {
      key: 'internalTransportation',
      label: t('expenses.internalTransportation'),
      icon: Car,
      color: 'text-yellow-600'
    },
    {
      key: 'programFees',
      label: t('expenses.programFees'),
      icon: FileText,
      color: 'text-teal-600'
    },
    {
      key: 'miscellaneous',
      label: t('expenses.miscellaneous'),
      icon: MoreHorizontal,
      color: 'text-gray-600'
    }
  ];

  useEffect(() => {
    if (editingExpense) {
      setFormData({
        type: editingExpense,
        amount: campaign.expenses[editingExpense]?.toString() || '',
        description: '',
        date: new Date().toISOString().split('T')[0]
      });
    }
  }, [editingExpense, campaign]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.type) {
      newErrors.type = t('expenses.validationTypeRequired');
    }

    if (!formData.amount) {
      newErrors.amount = t('expenses.validationAmountRequired');
    } else if (isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
      newErrors.amount = t('expenses.validationAmountInvalid');
    }

    if (!formData.date) {
      newErrors.date = t('expenses.validationDateRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API call
      
      onSave({
        type: formData.type,
        amount: parseFloat(formData.amount),
        description: formData.description,
        date: formData.date
      });
    } catch (error) {
      console.error('Error saving expense:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const selectedExpenseType = expenseTypes.find(type => type.key === formData.type);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {editingExpense ? t('expenses.editExpense') : t('expenses.addExpense')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Campaign Info */}
        <div className="p-6 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
          <h3 className="font-medium text-gray-900 dark:text-white mb-1">
            {campaign.campaignName}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t(`campaigns.${campaign.campaignType}`)} • {campaign.maxCapacity} {t('campaigns.travelers')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Expense Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('expenses.expenseType')} *
            </label>
            <select
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              disabled={editingExpense}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                errors.type ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              } ${editingExpense ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <option value="">{t('expenses.selectExpenseType')}</option>
              {expenseTypes.map((type) => (
                <option key={type.key} value={type.key}>
                  {type.label}
                </option>
              ))}
            </select>
            {errors.type && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.type}</p>
            )}
          </div>

          {/* Selected Type Display */}
          {selectedExpenseType && (
            <div className="flex items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              {React.createElement(selectedExpenseType.icon, {
                className: `h-5 w-5 ${selectedExpenseType.color} mr-3`
              })}
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {selectedExpenseType.label}
              </span>
            </div>
          )}

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('expenses.amount')} *
            </label>
            <div className="relative">
              <Coins className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => handleInputChange('amount', e.target.value)}
                placeholder={t('expenses.amountPlaceholder')}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  errors.amount ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
            </div>
            {errors.amount && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.amount}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('expenses.description')}
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder={t('expenses.descriptionPlaceholder')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
            />
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('expenses.date')} *
            </label>
            <DateInput

 value={formData.date}
 onChange={(e) => handleInputChange('date', e.target.value)}
 className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
 errors.date ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
 }`}
 />
            {errors.date && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.date}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {t('common.save')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpenseForm;